import { endOfYear, startOfYear, todayIso } from '@apka/core'
import { useEffect, useState } from 'react'
import { BladApi, api } from '../dane/api.js'
import {
  usunWpisSlownika,
  utworzWpisSlownika,
  zmienWpisSlownika,
} from '../dane/mutacje.js'
import {
  sprawdzPowiadomienia,
  wlaczPowiadomienia,
  wylaczPowiadomienia,
} from '../dane/powiadomienia.js'
import type { StanPowiadomien } from '../dane/powiadomienia.js'
import { useSesja } from '../dane/sesja.js'
import type { WpisLogu } from '../dane/typy.js'
import { uzyjDostawcow, uzyjKategorie, uzyjKlientow } from '../dane/widoki.js'
import { IkonaKosz, IkonaPobierz } from '../ui/Ikony.js'
import { Modal } from '../ui/Modal.js'
import { Pole } from '../ui/Pole.js'
import { Potwierdzenie } from '../ui/Potwierdzenie.js'
import { Przycisk } from '../ui/Przycisk.js'
import { useToast } from '../ui/Toast.js'
import { znacznikCzasu } from '../ui/format.js'
import './wiecej.css'

const KOLORY = [
  '#D9600F',
  '#8B5CF6',
  '#0EA5E9',
  '#22C55E',
  '#EAB308',
  '#EC4899',
  '#64748B',
] as const

function losowyKolor(indeks: number): string {
  return KOLORY[indeks % KOLORY.length] ?? '#D9600F'
}

interface WpisSlownika {
  id: string
  name: string
  color?: string
}

interface PropsSlownika {
  tytul: string
  opis: string
  slownik: 'categories' | 'vendors' | 'clients'
  wpisy: WpisSlownika[]
  zKolorem: boolean
}

function Slownik({ tytul, opis, slownik, wpisy, zKolorem }: PropsSlownika) {
  const [nazwa, ustawNazwe] = useState('')
  const [pracuje, ustawPracuje] = useState(false)
  const [doZmiany, ustawDoZmiany] = useState<WpisSlownika | null>(null)
  const [doUsuniecia, ustawDoUsuniecia] = useState<WpisSlownika | null>(null)
  const toast = useToast()

  async function dodaj() {
    const czysta = nazwa.trim()
    if (czysta === '') return
    ustawPracuje(true)
    try {
      const dane: Record<string, unknown> = { name: czysta }
      if (zKolorem) dane.color = losowyKolor(wpisy.length)
      if (slownik === 'categories') dane.type = 'expense'
      await utworzWpisSlownika(slownik, dane)
      ustawNazwe('')
    } catch (problem) {
      toast.pokazBlad(problem instanceof BladApi ? problem.message : 'Nie udało się dodać')
    } finally {
      ustawPracuje(false)
    }
  }

  async function zapiszNazwe(wpis: WpisSlownika, nowa: string) {
    const oczyszczona = nowa.trim()
    if (oczyszczona === '' || oczyszczona === wpis.name) {
      ustawDoZmiany(null)
      return
    }
    try {
      await zmienWpisSlownika(slownik, wpis.id, { name: oczyszczona })
    } catch (problem) {
      toast.pokazBlad(problem instanceof BladApi ? problem.message : 'Nie udało się zmienić')
    } finally {
      ustawDoZmiany(null)
    }
  }

  async function usun(wpis: WpisSlownika) {
    try {
      await usunWpisSlownika(slownik, wpis.id)
    } catch (problem) {
      toast.pokazBlad(problem instanceof BladApi ? problem.message : 'Nie udało się usunąć')
    } finally {
      ustawDoUsuniecia(null)
    }
  }

  return (
    <section className="blok karta">
      <header className="blok__naglowek">
        <h2>{tytul}</h2>
        <p>{opis}</p>
      </header>

      {wpisy.length === 0 ? (
        <p className="blok__pusto">Jeszcze nic tu nie ma.</p>
      ) : (
        <ul className="slownik">
          {wpisy.map((wpis) => (
            <li key={wpis.id} className="slownik__wpis">
              {wpis.color === undefined ? null : (
                <span
                  className="kropka-kategorii"
                  style={{ background: wpis.color }}
                  aria-hidden="true"
                />
              )}
              <button type="button" className="slownik__nazwa" onClick={() => ustawDoZmiany(wpis)}>
                {wpis.name}
              </button>
              <button
                type="button"
                className="slownik__usun"
                onClick={() => ustawDoUsuniecia(wpis)}
                aria-label={`Usuń ${wpis.name}`}
              >
                <IkonaKosz width={16} height={16} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="slownik__dodaj">
        <input
          value={nazwa}
          onChange={(zdarzenie) => ustawNazwe(zdarzenie.target.value)}
          onKeyDown={(zdarzenie) => {
            if (zdarzenie.key === 'Enter') void dodaj()
          }}
          placeholder="Nowa nazwa"
          aria-label={`Dodaj do: ${tytul}`}
        />
        <Przycisk rozmiar="maly" onClick={() => void dodaj()} pracuje={pracuje}>
          Dodaj
        </Przycisk>
      </div>

      {doZmiany !== null ? (
        <ZmienNazwe
          wpis={doZmiany}
          onZapisz={(nowa) => zapiszNazwe(doZmiany, nowa)}
          onAnuluj={() => ustawDoZmiany(null)}
        />
      ) : null}

      {doUsuniecia !== null ? (
        <Potwierdzenie
          tytul="Usunąć wpis?"
          tresc={`„${doUsuniecia.name}” zniknie z listy. Pozycje, które go używały, zostaną bez przypisania.`}
          etykietaPotwierdz="Usuń"
          niebezpieczne
          onPotwierdz={() => usun(doUsuniecia)}
          onAnuluj={() => ustawDoUsuniecia(null)}
        />
      ) : null}
    </section>
  )
}

/** Zmiana nazwy w modalu — `window.prompt` wygląda obco i nie da się go stylować. */
function ZmienNazwe({
  wpis,
  onZapisz,
  onAnuluj,
}: {
  wpis: WpisSlownika
  onZapisz(nowa: string): void | Promise<void>
  onAnuluj(): void
}) {
  const [nazwa, ustawNazwe] = useState(wpis.name)
  const [pracuje, ustawPracuje] = useState(false)

  async function zapisz() {
    if (pracuje) return
    ustawPracuje(true)
    try {
      await onZapisz(nazwa)
    } finally {
      ustawPracuje(false)
    }
  }

  return (
    <Modal
      tytul="Zmień nazwę"
      onZamknij={onAnuluj}
      stopka={
        <>
          <Przycisk wariant="drugi" onClick={onAnuluj}>
            Anuluj
          </Przycisk>
          <Przycisk wariant="glowny" pracuje={pracuje} onClick={() => void zapisz()}>
            Zapisz
          </Przycisk>
        </>
      }
    >
      <Pole
        etykieta="Nazwa"
        value={nazwa}
        autoFocus
        onChange={(zdarzenie) => ustawNazwe(zdarzenie.target.value)}
        onKeyDown={(zdarzenie) => {
          if (zdarzenie.key === 'Enter') void zapisz()
        }}
      />
    </Modal>
  )
}

function Eksport() {
  const rok = Number(todayIso().slice(0, 4))
  const [od, ustawOd] = useState(startOfYear(rok))
  const [doDnia, ustawDo] = useState(endOfYear(rok))
  const [pracuje, ustawPracuje] = useState(false)
  const toast = useToast()

  /**
   * Plik pobieramy przez klienta API, bo zwykły odnośnik nie doniesie
   * nagłówka z tokenem dostępowym.
   */
  async function pobierz() {
    ustawPracuje(true)
    try {
      const tresc = await api.get<string>(`/api/export/payments.csv?from=${od}&to=${doDnia}`)
      const url = URL.createObjectURL(new Blob([tresc], { type: 'text/csv;charset=utf-8' }))
      const odnosnik = document.createElement('a')
      odnosnik.href = url
      odnosnik.download = `platnosci-${od}-${doDnia}.csv`
      odnosnik.click()
      URL.revokeObjectURL(url)
    } catch (problem) {
      toast.pokazBlad(problem instanceof BladApi ? problem.message : 'Nie udało się pobrać pliku')
    } finally {
      ustawPracuje(false)
    }
  }

  return (
    <section className="blok karta">
      <header className="blok__naglowek">
        <h2>Eksport do CSV</h2>
        <p>Plik otwiera się w Excelu i Arkuszach Google</p>
      </header>
      <div className="siatka-pol siatka-pol--dwie">
        <Pole
          etykieta="Od"
          type="date"
          value={od}
          onChange={(zdarzenie) => ustawOd(zdarzenie.target.value)}
        />
        <Pole
          etykieta="Do"
          type="date"
          value={doDnia}
          onChange={(zdarzenie) => ustawDo(zdarzenie.target.value)}
        />
      </div>
      <Przycisk onClick={() => void pobierz()} pracuje={pracuje}>
        <IkonaPobierz width={17} height={17} />
        Pobierz płatności
      </Przycisk>
    </section>
  )
}

const OPISY_POWIADOMIEN: Record<StanPowiadomien, string> = {
  niedostepne: 'Ta przeglądarka nie obsługuje powiadomień',
  'wylaczone-na-serwerze': 'Serwer nie ma skonfigurowanych kluczy VAPID',
  odmowa: 'Powiadomienia zablokowane w ustawieniach przeglądarki',
  wlaczone: 'Włączone na tym urządzeniu',
  'gotowe-do-wlaczenia': 'Przypomnienie o zaległych i dzisiejszych płatnościach',
}

function Powiadomienia() {
  const [stan, ustawStan] = useState<StanPowiadomien | null>(null)
  const [pracuje, ustawPracuje] = useState(false)
  const toast = useToast()

  useEffect(() => {
    let aktualne = true
    void sprawdzPowiadomienia().then(
      (wynik) => {
        if (aktualne) ustawStan(wynik)
      },
      () => {
        if (aktualne) ustawStan('niedostepne')
      },
    )
    return () => {
      aktualne = false
    }
  }, [])

  async function przelacz() {
    ustawPracuje(true)
    try {
      const wynik = stan === 'wlaczone' ? await wylaczPowiadomienia() : await wlaczPowiadomienia()
      ustawStan(wynik)
      if (wynik === 'wlaczone') toast.pokaz('Powiadomienia włączone')
      if (wynik === 'odmowa') toast.pokazBlad('Przeglądarka odmówiła zgody na powiadomienia')
    } catch (problem) {
      toast.pokazBlad(problem instanceof BladApi ? problem.message : 'Nie udało się zmienić')
    } finally {
      ustawPracuje(false)
    }
  }

  const mozna = stan === 'wlaczone' || stan === 'gotowe-do-wlaczenia'

  return (
    <section className="blok karta">
      <header className="blok__naglowek">
        <h2>Powiadomienia</h2>
        <p>{stan === null ? 'Sprawdzam…' : OPISY_POWIADOMIEN[stan]}</p>
      </header>
      <Przycisk
        onClick={() => void przelacz()}
        pracuje={pracuje}
        disabled={!mozna}
        wariant={stan === 'wlaczone' ? 'drugi' : 'glowny'}
      >
        {stan === 'wlaczone' ? 'Wyłącz na tym urządzeniu' : 'Włącz przypomnienia'}
      </Przycisk>
    </section>
  )
}

const NAZWY_AKCJI: Record<WpisLogu['action'], string> = {
  create: 'dodał',
  update: 'zmienił',
  delete: 'usunął',
}

const NAZWY_ENCJI: Record<string, string> = {
  items: 'pozycję',
  payments: 'płatność',
  categories: 'kategorię',
  vendors: 'dostawcę',
  clients: 'klienta',
}

function LogZmian() {
  const [wpisy, ustawWpisy] = useState<WpisLogu[] | null>(null)
  const [pracuje, ustawPracuje] = useState(false)
  const toast = useToast()

  async function wczytaj() {
    ustawPracuje(true)
    try {
      ustawWpisy(await api.get<WpisLogu[]>('/api/changelog'))
    } catch (problem) {
      toast.pokazBlad(problem instanceof BladApi ? problem.message : 'Nie udało się wczytać logu')
    } finally {
      ustawPracuje(false)
    }
  }

  return (
    <section className="blok karta">
      <header className="blok__naglowek">
        <h2>Log zmian</h2>
        <p>Kto co zmienił i kiedy</p>
      </header>

      {wpisy === null ? (
        <Przycisk onClick={() => void wczytaj()} pracuje={pracuje}>
          Pokaż ostatnie zmiany
        </Przycisk>
      ) : wpisy.length === 0 ? (
        <p className="blok__pusto">Brak zapisanych zmian.</p>
      ) : (
        <ul className="log">
          {wpisy.map((wpis) => (
            <li key={wpis.id} className="log__wpis">
              <span className="log__opis">
                Ktoś {NAZWY_AKCJI[wpis.action]} {NAZWY_ENCJI[wpis.entity] ?? wpis.entity}
              </span>
              <span className="log__czas">{znacznikCzasu(wpis.at)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

export function Wiecej() {
  const { uzytkownik, wyloguj } = useSesja()
  const kategorie = uzyjKategorie() ?? []
  const dostawcy = uzyjDostawcow() ?? []
  const klienci = uzyjKlientow() ?? []

  return (
    <>
      <header className="naglowek-ekranu">
        <div>
          <h1>Więcej</h1>
          <p>{uzytkownik?.email ?? ''}</p>
        </div>
      </header>

      <div className="bloki">
        <Slownik
          tytul="Kategorie"
          opis="Grupują koszty na wykresach i listach"
          slownik="categories"
          wpisy={kategorie}
          zKolorem
        />
        <Slownik
          tytul="Dostawcy"
          opis="Hetzner, Anthropic, OVH…"
          slownik="vendors"
          wpisy={dostawcy}
          zKolorem={false}
        />
        <Slownik
          tytul="Klienci"
          opis="Koszty ponoszone w imieniu klienta"
          slownik="clients"
          wpisy={klienci}
          zKolorem
        />
        <Powiadomienia />
        <Eksport />
        <LogZmian />

        <section className="blok karta">
          <header className="blok__naglowek">
            <h2>Sesja</h2>
            <p>Zalogowano jako {uzytkownik?.name ?? '—'}</p>
          </header>
          <Przycisk wariant="niebezpieczny" onClick={() => void wyloguj()}>
            Wyloguj
          </Przycisk>
        </section>
      </div>
    </>
  )
}
